"use strict";


import {mount} from "@21torr/dune/mount.js";
import {
    isBlank,
    isBoolean,
    isEmpty,
    isFunction,
    isNull,
    isNumber,
    isObject
} from "./type-utils.js";
import * as _ from 'underscore';

/**
 * Shorthand to attach both a click and touchend-event to a HTML-Element
 * @param {HTMLElement} element
 * @param {Function} func
 * @param {Number} debounceTime=250 Time to wait till function is executed
 * @param {Boolean} executeImmediately=true If true, will execute func immediately but still prevent further executions for debouncetile
 */
export function attachTouchClickEventListener(element, func, debounceTime = 250, executeImmediately = true) {

    if (isEmpty(element) || isEmpty(func)) {
        return;
    }

    if (!isObject(element) || !isFunction(element['addEventListener']) || !isFunction(func) || !isNumber(debounceTime) || !isBoolean(executeImmediately)) {
        console.warn('Invalid signature for attachTouchClickEventListener', element, func, debounceTime, executeImmediately);
        return
    }

    let touchMoving = false;

    let handler = _.debounce((...args) => {
        if (!touchMoving) {
            func.apply(this, args);
        }

        touchMoving = false;

    }, debounceTime, executeImmediately);


    ['click', 'touchend'].forEach(event => {
        element.addEventListener(event, handler);
    });


    element.addEventListener("touchmove", e => {
        touchMoving = true;
    }, {passive: true})

    return handler;
}

/**
 * Shorthand to attach a input, keydown and change-event to a HTML-(Input)-Element
 * can be used to catch any type of input value modification
 * @param {HTMLElement} element
 * @param {Function} func * @param {Number} debounceTime=250 Time to wait till function is executed
 * @param {Boolean} executeImmediately=true If true, will execute func immediately but still prevent further executions for debouncetile
 */
export function attachInputValueChangeEventListener(element, func, debounceTime = 250, executeImmediately = true) {

    if (isEmpty(element) || isEmpty(func)) {
        return;
    }

    if (!isObject(element) || !isFunction(element['addEventListener']) || !isFunction(func) || !isNumber(debounceTime) || !isBoolean(executeImmediately)) {
        console.warn('Invalid signature for attachTypeChangeEventListener', element, func, debounceTime, executeImmediately);
        return
    }

    let handler = _.debounce(func, debounceTime, executeImmediately);

    ['input', 'keydown', 'change'].forEach(event => {
        element.addEventListener(event, handler);
    });

    return handler;
}

/**
 * Press a button if a keyup-event with is caught on certain elements, as defined by "elementSelector"
 *
 * Useful to submit a form, press a button when a user enters something into an input field and then presses "Enter"
 *
 * @param button {HTMLElement} Element to submit click event on when a matching keyup event is detected
 * @param elementSelector {String} CSS-Selector for elements to track keyup events on
 * @param {Boolean} preventDefault if true, will call event.stopPropagation and event.preventDefault
 */
export function pressButtonOnEnter(button, elementSelector, preventDefault = false) {
    if (isNull(button) || !isFunction(button.click) || isBlank(elementSelector)) {
        return;
    }

    mount(elementSelector, (element) => {
        element.addEventListener('keyup', _.debounce(keyUpEvent => {
            if (keyUpEvent.key && keyUpEvent.key == 'Enter') {
                button.click();

                if (!!preventDefault) {
                    keyUpEvent.stopPropagation();
                    keyUpEvent.preventDefault();
                    return false;
                }
            }
        }), 250, true);
    });
}

/**
 * Executes the passed function after document load (load event)
 * If the document has already been loaded, executes the function immediately
 *
 * @param callback {Function}
 */
export function executeOnLoad(callback) {

    if (!isFunction(callback)) {
        return;
    }

    if (document.readyState !== 'complete') {
        window.addEventListener('load', () => {
            callback();
        })
    } else {
        callback();
    }
}